import { IconButton, Stack } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"

import { cellStateFor, themeDishRow } from "../../weekMenuLib"
import { ThemeDishValues, WeekMenuCellState } from "../../weekMenuTypes"
import { CellAllergensField } from "../cell/CellAllergensField"
import { CellDescriptionField } from "../cell/CellDescriptionField"
import { CellImageField } from "../cell/CellImageField"
import { CellMarketingField } from "../cell/CellMarketingField"
import { CellPriceField } from "../cell/CellPriceField"
import { CellStatusTag } from "../cell/CellStatusTag"
import { CellTitleField } from "../cell/CellTitleField"

const useStyles = makeStyles()((theme) => ({
    // The regular day cell's look in a rounded card; the whole card border carries the status color.
    cell: {
        position: "relative",
        minHeight: "9rem",
        padding: ".625rem .625rem .75rem",
        border: `1px solid ${theme.palette.tailwind.orange[200]}`,
        borderRadius: ".75rem",
        background: theme.palette.background.paper,
    },
    cellFilled: {
        borderColor: theme.palette.tailwind.green[600],
    },
    cellNeedsFill: {
        borderColor: theme.palette.tailwind.amber[500],
        background: theme.palette.tailwind.amber[50],
    },
    statusRow: {
        width: "100%",
    },
    removeButton: {
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: ".25rem",
        color: theme.palette.tailwind.slate[400],
        "& svg": {
            fontSize: ".875rem",
        },
        "&:hover": {
            color: theme.palette.error.main,
        },
    },
}))

type Props = Readonly<{
    values: ThemeDishValues
    onChange: (values: Partial<ThemeDishValues>) => void
    onRemove: () => void
}>

export function ThemeDishCard({ values, onChange, onRemove }: Props) {
    const { classes, cx } = useStyles()

    const row = themeDishRow(values.title)
    const state = cellStateFor(row, values, false)
    const showMissing = state === WeekMenuCellState.NEEDS_FILL

    const stateClasses: Record<WeekMenuCellState, string | undefined> = {
        [WeekMenuCellState.FILLED]: classes.cellFilled,
        [WeekMenuCellState.NEEDS_FILL]: classes.cellNeedsFill,
        [WeekMenuCellState.TEMPLATE_DEFAULT]: undefined,
        [WeekMenuCellState.EMPTY]: undefined,
    }

    return (
        <Stack spacing=".375rem" className={cx(classes.cell, stateClasses[state])}>
            <CellTitleField
                value={values.title}
                hasError={showMissing && values.title.trim() === ""}
                onChange={(title) => onChange({ title })}
                onPickDish={(dish) => onChange({ ...dish })}
            />

            <CellDescriptionField
                dishName={values.title}
                value={values.description}
                hasError={false}
                isOptional={false}
                onChange={(description) => onChange({ description })}
            />

            <CellAllergensField
                value={values.allergens}
                hasError={false}
                isReadOnly={false}
                isOptional={false}
                onChange={(allergens) => onChange({ allergens })}
            />

            <CellMarketingField
                value={values.marketingTag}
                hasError={false}
                onChange={(marketingTag) => onChange({ marketingTag })}
            />

            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing=".5rem"
                className={classes.statusRow}
            >
                <CellPriceField
                    value={values.price}
                    hasError={false}
                    isLocked={false}
                    isSameAllWeek={false}
                    onChange={(price) => onChange({ price })}
                />
                <CellImageField
                    photo={values.photo}
                    hasError={false}
                    isLocked={false}
                    onChange={(photo) => onChange({ photo })}
                />
            </Stack>

            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing=".5rem"
                className={classes.statusRow}
            >
                <CellStatusTag row={row} values={values} state={state} />
                <LocalizedStrict id="merchant-week-menu-theme-dish-remove-button" attrs={{ "aria-label": true }}>
                    <IconButton
                        size="small"
                        aria-label="Remove theme dish"
                        className={classes.removeButton}
                        onClick={onRemove}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )
}
